// Cumbre — la mascota del chat de Atlas.
//
// Silueta llena con el gradiente de marca (primario → acento, siguen al branding) y los ojos CALADOS con
// una `mask`: los huecos son transparentes y dejan pasar el fondo, así que la misma
// pieza sirve sobre el azul oscuro de la marca y sobre el fondo claro del sheet. Sin
// boca.
//
// Las animaciones y los tres estados viven en `src/cumbre.css` (importado en
// `main.tsx`): animan `r`, `cx` y `cy`, que son propiedades geométricas de SVG y no se
// pueden expresar con utilidades de Tailwind.
import { useId } from 'react';

const PATH =
  'M24.5 150.016C14.5 164.516 0.440875 192.014 0.0152099 209.58C-0.868865 245.982 36.9499 272.216 70.9704 258.789C87.3094 252.337 94.8077 239.073 105.089 226.103C111.769 217.662 120.151 209.515 131.415 208.178C136.635 207.553 141.5 207.516 147.5 207.516C152.5 207.516 157.071 207.553 162.291 208.178C173.555 209.515 181.937 217.662 188.617 226.103C198.898 239.073 206.397 252.337 222.736 258.789C256.756 272.216 294.575 245.982 293.691 209.58C293.265 192.014 279.206 164.516 269.206 150.016L250.371 122.502L182.036 18.3143C174.275 6.48442 160.711 -0.375201 146.5 0.0158691C132.289 -0.375201 119.431 6.48442 111.67 18.3143L43.3349 122.502L24.5 150.016Z';

/** La silueta de Cumbre (viewBox 294×263), para quien la use como forma: la cordillera
 *  del onboarding de marca (ADR-2026-10-01-onboarding-v2-cumbre-guia). */
export const SILUETA_DE_CUMBRE = PATH;

export interface CumbreMascotProps {
  /**
   * `idle` — reposo: flota y parpadea cada tanto.
   * `thinking` — ojos chicos que titilan alternados, sin parpadeo.
   * `answering` — ojos grandes, mirada abierta, sin parpadeo.
   */
  state?: 'idle' | 'thinking' | 'answering';
  /** Ancho en px. El alto sale de la proporción del viewBox (294×263). */
  size?: number;
  /**
   * Clases extra. Para un tamaño POR BREAKPOINT va acá (`w-[84px] md:w-[120px] h-auto`):
   * el CSS le gana a los atributos `width`/`height`, que quedan de respaldo.
   */
  className?: string;
  /**
   * `true` cuando la mascota es decorativa y al lado hay un título que ya dice lo
   * mismo — ahí anunciarla es repetir. Por defecto se anuncia como "Asistente Atlas".
   */
  decorativa?: boolean;
  /**
   * LA MASCOTA COMO ÍCONO: de CONTORNO, del color del texto de alrededor
   * (`currentColor`) — ADR-2026-09-22-la-mascota-es-el-icono-del-agente-en-la-barra,
   * ADR-2026-09-22-la-mascota-icono-va-de-contorno.
   *
   * Es para donde la mascota hace de ícono y no de mascota: la barra inferior del
   * teléfono, donde los otros cuatro lugares son glifos de Material Symbols **de
   * contorno** y de un color que cambia con el estado (gris al 60% quieto, azul de marca
   * el activo). Rellena —con gradiente o con un gris plano— el Agente era una mancha
   * entre cuatro dibujos de línea.
   *
   * De contorno la silueta va trazada y sin relleno, así que los ojos no pueden ser
   * huecos calados: se dibujan como dos PUNTOS del mismo color, que es lo que hace
   * cualquier ícono de cara a este tamaño. El `viewBox` se agranda lo que mide el trazo
   * para que no se coma medio contorno en el borde.
   *
   * Y va QUIETA (ver `.cumbre-icono` en `cumbre.css`): sin flotar, sin pestañear y sin la
   * sombra azul. Donde hace de glifo tiene que portarse como un glifo.
   */
  comoIcono?: boolean;
}

export function CumbreMascot({
  state = 'idle',
  size = 180,
  className,
  decorativa = false,
  comoIcono = false,
}: CumbreMascotProps) {
  // El `mask` y el gradiente se referencian por id, y los ids son GLOBALES al
  // documento: con dos mascotas en pantalla, la segunda pisaría a la primera y las dos
  // usarían la misma máscara. `useId` da uno por instancia (sin los `:` que rompen el
  // selector de `url(#…)`).
  const uid = useId().replace(/:/g, '');
  const g = `cumbre-g-${uid}`;
  const m = `cumbre-m-${uid}`;

  /* El trazo del contorno, en unidades del `viewBox`: 22 sobre 294 de ancho son ~1,8px
     cuando el ícono se dibuja a 24, que es el grosor de los glifos de la barra (`wght
     400`). Y el `viewBox` se corre medio trazo para afuera: el path llega hasta el borde,
     así que trazado sin este aire se vería cortado arriba y abajo. */
  const TRAZO = 22;
  if (comoIcono) {
    const a = TRAZO / 2;
    return (
      <svg
        className={['cumbre', 'cumbre-icono', className].filter(Boolean).join(' ')}
        data-state={state}
        width={size}
        height={Math.round((size * (263 + TRAZO)) / (294 + TRAZO))}
        viewBox={`${-a} ${-a} ${294 + TRAZO} ${263 + TRAZO}`}
        fill="none"
        {...(decorativa
          ? { 'aria-hidden': true as const }
          : { role: 'img', 'aria-label': 'Asistente Atlas' })}
      >
        <path
          d={PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth={TRAZO}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Los ojos, dos puntos: sin relleno no hay de dónde calarlos. Van llenos y del
            mismo color que el trazo, como los tiene cualquier ícono de cara. */}
        <circle className="cumbre-eye cumbre-eye-l" cx="110" cy="145" r="20" fill="currentColor" />
        <circle className="cumbre-eye cumbre-eye-r" cx="184" cy="145" r="20" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg
      className={['cumbre', className].filter(Boolean).join(' ')}
      data-state={state}
      width={size}
      height={Math.round((size * 263) / 294)}
      viewBox="0 0 294 263"
      fill="none"
      {...(decorativa
        ? { 'aria-hidden': true as const }
        : { role: 'img', 'aria-label': 'Asistente Atlas' })}
    >
      <defs>
        <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--brand-primary)' }} />
          <stop offset="1" style={{ stopColor: 'var(--brand-accent)' }} />
        </linearGradient>
        <mask id={m}>
          <rect x="0" y="0" width="294" height="263" fill="#fff" />
          <g className="cumbre-blink">
            <circle
              className="cumbre-eye cumbre-eye-l"
              cx="110"
              cy="145"
              r="22"
              fill="#000"
            />
            <circle
              className="cumbre-eye cumbre-eye-r"
              cx="184"
              cy="145"
              r="22"
              fill="#000"
            />
          </g>
        </mask>
      </defs>
      <path mask={`url(#${m})`} fill={`url(#${g})`} d={PATH} />
    </svg>
  );
}

export default CumbreMascot;
